"use client";

import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import timezone from "dayjs/plugin/timezone";
import { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { useMatches } from "@/hooks/useMatches";
import { useFilterOdds } from "@/hooks/useFilterOdds";
import { useMatchOdds } from "@/hooks/useMatchOdds";
import { splitCorrectScoresListToSections, formatCorrectScoresForGrid } from "@/utils/matchInfo";
import { LIVE_STATUSES } from "@/contants";
import { useLiveDataForMatch } from "@/hooks/useLiveData";
import { useMatchData } from "@/hooks/useMatchData";
import KdbKbvMatchMinute from "../KdbKbvMatchMinute";
import KdbOtherBetsDropdown from "../KdbOtherBetsDropdown/KdbOtherBetsDropdown";
import KdbOdds3 from "./KdbOdds3";
import ViuGoTipsAiModal from "../ViuGoTipsAiModal";
import viugoTipsAiSparkle from "@/assets/images/viugo/kbv-v16-tips-ai-sparkle.svg";

dayjs.extend(utc);
dayjs.extend(timezone);

export default function KdbMatch({ match, leagueName, leagueLogo, oddType = "hdp" }) {
  const [showOther, setShowOther] = useState(false);
  const [otherOddsData, setOtherOddsData] = useState(null);
  const [loadingOtherOdds, setLoadingOtherOdds] = useState(false);
  const [isDesktop, setIsDesktop] = useState(false);
  const [openTipsAiModal, setOpenTipsAiModal] = useState(false);
  const [tipsAiInsightData, setTipsAiInsightData] = useState(null);
  const [loadingTipsAiInsight, setLoadingTipsAiInsight] = useState(false);
  const { getMatchOdds, getMatchInsight } = useMatches();
  const { getOdds, getScore } = useLiveDataForMatch(match?.id);
  const liveOdds = getOdds();
  const liveScore = getScore();
  const statusNum = liveScore?.status !== undefined ? Number(liveScore.status) : Number(match.status);
  const { currentHomeScore, currentAwayScore, currentHomeCorner, currentAwayCorner, homeYellow, awayYellow, homeRed, awayRed } =
    useMatchData(liveScore, match);
  const isLive = LIVE_STATUSES.includes(statusNum);
  const companyIdOdds = useSelector((state) => state.bookmakerReducer);
  const filterOdds = useFilterOdds(match, liveOdds, null, companyIdOdds);

  const {
    handicapValue, homeHandicapOdds, awayHandicapOdds, homeHandicapTrend, awayHandicapTrend,
    handicapHalfValue, homeHandicapHalfOdds, awayHandicapHalfOdds, homeHandicapHalfTrend, awayHandicapHalfTrend,
    overUnderValue, overOdds, underOdds, overTrend, underTrend,
    homeEuropeOdds, awayEuropeOdds, instantDrawOdds, homeEuropeTrend, awayEuropeTrend, drawTrend,
  } = useMatchOdds(match, filterOdds, companyIdOdds);

  const ftHandicap = <KdbOdds3 left={homeHandicapOdds} middle={handicapValue} right={awayHandicapOdds} leftTrend={homeHandicapTrend} rightTrend={awayHandicapTrend} fullWidth={!isDesktop} />;
  const ftEurope = <KdbOdds3 left={homeEuropeOdds} middle={instantDrawOdds} right={awayEuropeOdds} leftTrend={homeEuropeTrend} middleTrend={drawTrend} rightTrend={awayEuropeTrend} fullWidth={!isDesktop} />;
  const ftOu = <KdbOdds3 left={overOdds} middle={overUnderValue} right={underOdds} leftTrend={overTrend} rightTrend={underTrend} fullWidth={!isDesktop} />;
  const correctScoresListFull = useMemo(
    () => (otherOddsData?.correctScoresOdds ? formatCorrectScoresForGrid(otherOddsData.correctScoresOdds) : []),
    [otherOddsData],
  );
  const correctScoresFull = useMemo(() => splitCorrectScoresListToSections(correctScoresListFull), [correctScoresListFull]);
  const totalCornersOdds = useMemo(() => (Array.isArray(otherOddsData?.totalCornersOdds) ? otherOddsData.totalCornersOdds : []), [otherOddsData]);
  const hasOtherData = useMemo(
    () => otherOddsData?.goalOddEvenOdds?.length > 0 || correctScoresListFull.length > 0 || totalCornersOdds.length > 0,
    [otherOddsData, correctScoresListFull.length, totalCornersOdds.length],
  );

  const handleOtherClick = (e) => {
    e.stopPropagation();
    setShowOther((prev) => !prev);
    if (!showOther && !otherOddsData && match?.id) {
      setLoadingOtherOdds(true);
      getMatchOdds(match.id, { include: "otherOdd" })
        .then((data) => { if (data?.success) setOtherOddsData(data.data); })
        .finally(() => setLoadingOtherOdds(false));
    }
  };

  const handleOpenTipsAiModal = async (e) => {
    e?.stopPropagation?.();
    setOpenTipsAiModal(true);
    if (tipsAiInsightData || loadingTipsAiInsight) return;
    setLoadingTipsAiInsight(true);
    try {
      const insightResponse = await getMatchInsight(match?.id);
      if (insightResponse?.success && insightResponse?.data) setTipsAiInsightData(insightResponse.data);
    } finally {
      setLoadingTipsAiInsight(false);
    }
  };

  const otherOddsCount = Number(match?.countOtherOdds ?? 0);
  const formattedGio = match?.matchTime ? dayjs(match.matchTime).format("HH:mm") : "--:--";
  const formattedDate = match?.matchTime ? dayjs(match.matchTime).format("DD/MM") : "--/--";
  const rankHome = match?.homePosition ?? match?.homeRank;
  const rankAway = match?.awayPosition ?? match?.awayRank;
  const derivedLeagueName = match?.league?.name || match?.leagueName || leagueName || "";
  const derivedLeagueLogo = match?.league?.logo || match?.leagueLogo || leagueLogo || "";
  const scoreText = `${currentHomeScore ?? 0} - ${currentAwayScore ?? 0}`;
  const modalStatusLabel = !isLive ? "Sắp diễn ra" : "Trực tiếp";
  const homeLogo = match?.homeTeam?.logo || match?.homeLogo;
  const awayLogo = match?.awayTeam?.logo || match?.awayLogo;
  const oddsView =
    oddType === "1x2" ? ftEurope : oddType === "ou" ? ftOu : ftHandicap;

  useEffect(() => {
    if (typeof window === "undefined") return;
    const mql = window.matchMedia("(min-width: 1024px)");
    const apply = () => setIsDesktop(!!mql.matches);
    apply();
    if (mql.addEventListener) mql.addEventListener("change", apply);
    else mql.addListener(apply);
    return () => {
      if (mql.removeEventListener) mql.removeEventListener("change", apply);
      else mql.removeListener(apply);
    };
  }, []);

  return (
    <>
      {isDesktop ? (
        <>
          <tr className="h-[36px] border-b border-solid border-[var(--theme-table-header-bg)] bg-white text-[12px] hover:kdb-row-hover">
            <td className="p-0 text-center text-[12px] text-gray-400 border-r border-solid border-[var(--theme-table-header-bg)]">
              <KdbKbvMatchMinute
                matchTime={match?.matchTime}
                status={statusNum}
                liveScore={liveScore}
                halfStartTime={match?.halfStartTime}
                className={`text-[12px] ${isLive ? "kdb-odds-down-text" : "text-gray-400"}`}
                showDate={false}
              />
            </td>
            <td className="p-0 pr-4 text-right border-r border-solid border-[var(--theme-table-header-bg)]">
              <span className="mx-1 text-[12px] text-[var(--theme-text-muted)]">{rankHome != null ? `[${rankHome}]` : ""}</span>
              <span className="text-[12px] font-medium text-gray-800">{match?.homeName}</span>
            </td>
            <td className="p-0 text-center text-[12px] font-bold text-gray-800 border-r border-solid border-[var(--theme-table-header-bg)]">{isLive ? scoreText : "Vs"}</td>
            <td className="p-0 pl-4 text-left border-r border-solid border-[var(--theme-table-header-bg)]">
              <span className="text-[12px] font-medium text-gray-800">{match?.awayName}</span>
              <span className="mx-1 text-[12px] text-[var(--theme-text-muted)]">{rankAway != null ? `[${rankAway}]` : ""}</span>
            </td>
            <td className="bg-white p-0" style={{ borderRight: "1px solid #dadada" }}>
              {oddsView}
            </td>
            <td className="p-0" style={{ borderLeft: 0 }}>
              <div className="flex items-center justify-center gap-1">
                <button type="button" onClick={handleOpenTipsAiModal} className="kbv-tips-btn-indigo-slate inline-flex h-6 items-center rounded border border-solid px-1 text-[12px] text-white">
                  <img src={viugoTipsAiSparkle} alt="" className="mr-1 h-3.5 w-3.5" />AI
                </button>
                <button type="button" onClick={handleOtherClick} className="rounded bg-[var(--theme-table-header-bg)] px-1 py-0.5 text-[12px] font-semibold">
                  {otherOddsCount}+
                </button>
              </div>
            </td>
          </tr>
          {showOther ? (
            <tr>
              <td colSpan={6} className="p-0">
                <KdbOtherBetsDropdown
                  loadingOtherOdds={loadingOtherOdds}
                  otherOddsData={otherOddsData}
                  hasOtherData={hasOtherData}
                  correctScoresListFull={correctScoresListFull}
                  correctScoresFull={correctScoresFull}
                  totalCornersOdds={totalCornersOdds}
                  odds={{ ...filterOdds, homeEuropeOdds, awayEuropeOdds, instantDrawOdds, homeEuropeTrend, awayEuropeTrend, drawTrend, homeHandicapHalfOdds, awayHandicapHalfOdds, homeHandicapHalfTrend, awayHandicapHalfTrend }}
                  showOther={showOther}
                />
              </td>
            </tr>
          ) : null}
        </>
      ) : (
        <div className="overflow-hidden rounded-[4px] bg-white shadow-sm">
          <div className="flex items-center gap-2 p-2">
            {derivedLeagueLogo ? <img src={derivedLeagueLogo} className="h-4 w-5" alt="" /> : null}
            <span className="text-[12px] font-medium text-gray-800">{derivedLeagueName || "Giải đấu"}</span>
          </div>
          <div className="p-1 pt-1">
            <div className="mb-1 flex items-center justify-between gap-2">
              <div className="flex-1 text-center">
                <div className="flex items-center justify-center gap-1">
                  <span className="text-[12px] font-medium text-gray-800">{match?.homeName}</span>
                  <img src={homeLogo || ""} className="h-5 w-5" alt="" />
                </div>
                <div className="mt-1 text-[12px] text-[var(--theme-text-muted)]">{rankHome != null ? `[${rankHome}]` : "-"}</div>
              </div>
              <div className="text-[12px] font-bold text-black">{isLive ? scoreText : "Vs"}</div>
              <div className="flex-1 text-center">
                <div className="flex items-center justify-center gap-1">
                  <img src={awayLogo || ""} className="h-5 w-5" alt="" />
                  <span className="text-[12px] font-medium text-gray-800">{match?.awayName}</span>
                </div>
                <div className="mt-1 text-[12px] text-[var(--theme-text-muted)]">{rankAway != null ? `[${rankAway}]` : "-"}</div>
              </div>
            </div>
            <div className="mb-3 text-center text-[12px] text-gray-800">
              <KdbKbvMatchMinute
                matchTime={match?.matchTime}
                status={statusNum}
                liveScore={liveScore}
                halfStartTime={match?.halfStartTime}
                className={`text-[12px] ${isLive ? "kdb-odds-down-text" : "text-gray-800"}`}
                showDate={!isLive}
              />
            </div>
            <div className="grid grid-cols-1 gap-2">{oddsView}</div>
            <div className="mt-2 grid grid-cols-2 gap-2">
              <button type="button" onClick={handleOpenTipsAiModal} className="kbv-tips-btn-indigo-slate flex h-8 items-center justify-center rounded-[4px] border border-solid text-[12px] text-white">
                <img src={viugoTipsAiSparkle} alt="" className="mr-1 h-3.5 w-3.5" />Tips AI
              </button>
              <button type="button" onClick={handleOtherClick} className="h-8 rounded-[4px] bg-[var(--theme-table-header-bg)] text-[12px] font-semibold">Kèo khác {otherOddsCount}+</button>
            </div>
            {showOther ? (
              <KdbOtherBetsDropdown
                loadingOtherOdds={loadingOtherOdds}
                otherOddsData={otherOddsData}
                hasOtherData={hasOtherData}
                correctScoresListFull={correctScoresListFull}
                correctScoresFull={correctScoresFull}
                totalCornersOdds={totalCornersOdds}
                odds={{ ...filterOdds, homeEuropeOdds, awayEuropeOdds, instantDrawOdds, homeEuropeTrend, awayEuropeTrend, drawTrend, homeHandicapHalfOdds, awayHandicapHalfOdds, homeHandicapHalfTrend, awayHandicapHalfTrend }}
                showOther={showOther}
              />
            ) : null}
          </div>
        </div>
      )}

      <ViuGoTipsAiModal
        open={openTipsAiModal}
        onClose={() => setOpenTipsAiModal(false)}
        match={match}
        matchInfo={{ formattedDate, formattedGio, isLive }}
        statsInfo={{ currentHomeScore, currentAwayScore, homeYellow, homeRed, awayYellow, awayRed, currentHomeCorner, currentAwayCorner }}
        oddsInfo={{
          handicapValue, homeHandicapOdds, awayHandicapOdds, overUnderValue, overOdds, underOdds,
          homeEuropeOdds, awayEuropeOdds, instantDrawOdds, handicapHalfValue, homeHandicapHalfOdds, awayHandicapHalfOdds,
        }}
        leagueName={derivedLeagueName}
        leagueLogo={derivedLeagueLogo}
        homeName={match?.homeName}
        homeLogo={homeLogo}
        awayName={match?.awayName}
        awayLogo={awayLogo}
        matchDate={formattedDate}
        matchTime={formattedGio}
        matchTimestamp={match?.matchTime}
        headerScore={scoreText}
        liveScoreToday={liveScore}
        matchStatusNum={statusNum}
        matchTimeUnix={match?.matchTime}
        halfStartTime={match?.halfStartTime}
        isMatchLive={isLive}
        statusLabel={modalStatusLabel}
        insightData={tipsAiInsightData}
        insightLoading={loadingTipsAiInsight}
      />
    </>
  );
}
